import { ROUTES } from 'Settings/route-strings';

/**
 * These are the initial settings for all of form fields.
 * Up top, we have the FIELDS object which has sensible defaults for
 * several commonly-used fields. In the default export, we have a set
 * of fields for each form, which include overrides.
 *
 * In hindsight, those should probably just be defined in a container
 * for the Form component. It’s a little bit of work, but more React-y
 * and much easier to understand/override.
 */

// These
const FIELDS = {
  given_name: {
    maxLength: '32',
    labelId: 'form.field.given_name.label',
    type: 'text',
    autocomplete: 'given-name',
  },
  family_name: {
    maxLength: '32',
    labelId: 'form.field.family_name.label',
    type: 'text',
    autocomplete: 'family-name',
  },
  username: {
    labelId: 'form.field.username.label',
    placeholderId: 'form.field.username.placeholder',
    type: 'text',
    autocomplete: 'username',
  },
  password: {
    labelId: 'form.field.password.label',
    type: 'text',
    autocomplete: 'off',
    noteId: 'form.field.password.note',
  },
  className: {
    placeholderId: 'form.field.className.placeholder',
    type: 'text',
  },
  schoolName: {
    labelId: 'form.field.schoolName.label',
    noteId: 'form.field.schoolName.note',
    type: 'text',
  },
  tipsTricks: {
    type: 'checkbox',
    labelId: 'form.field.tipsTricks.label',
  },
  acceptTerms: {
    type: 'checkbox',
    labelId: 'form.field.acceptTerms.label',
  },
  inviteList: {
    type: 'textlist',
    autocomplete: 'off',
  },
  credit_card: {
    type: 'stripeCard',
  },
  full_name: {
    type: 'text',
    automcomplete: 'name',
  },
};

// These are used twice — adding to a school or class
const addStudentFields = {
  given_name: {
    ...FIELDS.given_name,
    labelId: null,
    placeholderId: 'form.addStudentFields.field.given_name.placeholder',
    required: true,
    isFocused: true,
    fieldStyle: 'underline',
  },
  family_name: {
    ...FIELDS.family_name,
    labelId: null,
    placeholderId: 'form.addStudentFields.field.family_name.placeholder',
    required: false,
    isFocused: false,
    fieldStyle: 'underline',
  },
  username: {
    ...FIELDS.username,
    required: true,
    isFocused: false,
    fieldStyle: 'underline',
    minLength: 3,
    pattern: '^\\S*$',
    noteId: 'form.addStudentFields.field.username.note',
    labelId: null,
  },
};

const userSignupFields = {
  given_name: {
    ...FIELDS.given_name,
    required: true,
    isFocused: true,
    minLength: 1,
    fieldWidth: 'half',
  },
  family_name: {
    ...FIELDS.family_name,
    required: true,
    isFocused: false,
    minLength: 1,
    fieldWidth: 'half',
  },
  username: {
    ...FIELDS.username,
    type: 'email',
    labelId: 'form.userSignupFields.field.username.label',
    placeholderId: null,
    required: true,
    isFocused: false,
    pattern: '^\\S*$',
    fieldWidth: 'half',
    autocomplete: 'email',
  },
  password: {
    ...FIELDS.password,
    required: true,
    isFocused: false,
    fieldWidth: 'half',
    minLength: 8,
    pattern: '.*[^A-Za-z]+.*',
  },
  tipsTricks: {
    ...FIELDS.tipsTricks,
    value: false,
    isFocused: false,
    required: false,
    requiredTrue: false,
    fieldWidth: 'full',
  },
  acceptTerms: {
    ...FIELDS.acceptTerms,
    value: false,
    isFocused: false,
    requiredTrue: true,
    fieldWidth: 'full',
  },
};

const editUserFields = {
  given_name: {
    ...FIELDS.given_name,
    isFocused: true,
    minLength: 1,
    required: true,
    fieldWidth: 'half',
    fieldSize: 'small',
  },
  family_name: {
    ...FIELDS.family_name,
    isFocused: false,
    required: true,
    minLength: 1,
    fieldWidth: 'half',
    fieldSize: 'small',
  },
  username: {
    ...FIELDS.username,
    type: 'email',
    labelId: 'form.editUserFields.field.username.label',
    required: true,
    placeholderId: null,
    isFocused: false,
    pattern: '^\\S*$',
    fieldWidth: 'half',
    fieldSize: 'small',
    autocomplete: 'email',
    requiresSensitiveToken: true,
    minLength: 3,
  },
};

export default {
  createClass: {
    className: {
      ...FIELDS.className,
      required: true,
      isFocused: true,
    },
  },
  teacherLogin: {
    username: {
      ...FIELDS.username,
      labelId: 'form.teacherLogin.field.username.label',
      type: 'email',
      required: true,
      isFocused: true,
      placeholderId: null,
    },
    password: {
      ...FIELDS.password,
      required: true,
      isFocused: false,
      noteId: null,
      fieldLink: {
        text: 'Forgot your password?',
        textId: 'form.teacherLogin.field.password.field_link.text',
        to: ROUTES.RESET_PASSWORD,
      },
    },
  },
  studentSchoolCode: {
    accessCode: {
      ...FIELDS.accessCode,
      required: true,
      isFocused: true,
      labelId: 'form.studentSchoolCode.field.accessCode.label',
    },
  },
  studentLogin: {
    accessCode: {
      ...FIELDS.accessCode,
      hidden: true,
    },
    username: {
      ...FIELDS.username,
      labelId: 'form.studentSchoolCode.field.username.label',
      placeholderId: null,
      required: true,
      isFocused: true,
    },
  },
  // This form is only used for logged-in users, so the username
  // is populated from the store.
  loginAgain: {
    username: {
      ...FIELDS.username,
      hidden: true,
    },
    password: {
      ...FIELDS.password,
      required: true,
      noteId: null,
      isFocused: true,
    },
  },
  addStudentToClass: addStudentFields,
  addStudentToSchool: addStudentFields,
  acceptTeacherInvitation: userSignupFields,
  freeTrial: {
    ...userSignupFields,
    given_name: {
      ...userSignupFields.given_name,
      isFocused: true,
    },
    schoolName: {
      ...FIELDS.schoolName,
      required: true,
      isFocused: false,
      fieldWidth: 'full',
    },
    licenseType: {
      type: 'hidden',
    },
    source: {
      type: 'hidden',
    },
  },
  createAccount: {
    ...userSignupFields,
    given_name: {
      ...userSignupFields.given_name,
    },
    schoolName: {
      ...FIELDS.schoolName,
      required: true,
      fieldWidth: 'full',
    },
    licenseType: {
      type: 'hidden',
    },
  },
  forgotPassword: {
    username: {
      ...FIELDS.username,
      type: 'email',
      labelId: 'form.forgotPassword.field.username.label',
      required: true,
      isFocused: true,
      placeholderId: null,
    },
  },
  paymentUsername: {
    username: {
      ...FIELDS.username,
      type: 'email',
      labelId: 'form.forgotPassword.field.username.label',
      required: true,
      isFocused: true,
      placeholderId: null,
    },
  },
  resetPassword: {
    password: {
      ...FIELDS.password,
      labelId: 'form.resetPassword.field.password.label',
      required: true,
      isFocused: true,
      fieldWidth: 'half',
      minLength: 8,
      pattern: '.*[^A-Za-z]+.*',
    },
  },
  changePassword: {
    username: {
      ...FIELDS.username,
      hidden: true,
    },
    currentPassword: {
      ...FIELDS.password,
      labelId: 'form.changePassword.field.currentPassword.label',
      fieldWidth: 'half',
      required: true,
      isFocused: true,
      fieldSize: 'small',
      noteId: null,
    },
    newPassword: {
      ...FIELDS.password,
      labelId: 'form.changePassword.field.newPassword.label',
      required: true,
      isFocused: false,
      fieldWidth: 'half',
      minLength: 8,
      pattern: '.*[^A-Za-z]+.*',
      fieldSize: 'small',
    },
    newPasswordAgain: {
      ...FIELDS.password,
      labelId: 'form.changePassword.field.newPasswordAgain.label',
      isFocused: false,
      fieldWidth: 'half',
      mustMatch: 'newPassword',
      fieldSize: 'small',
      noteId: null,
    },
  },
  inviteMultipleTeachers: {
    inviteList: {
      ...FIELDS.inviteList,
      required: true,
      isFocused: true,
    },
  },
  inviteSingleTeacher: {
    username: {
      ...FIELDS.username,
      type: 'email',
      labelId: null,
      required: true,
      isFocused: true,
      placeholderId: 'form.inviteSingleTeacher.field.username.placeholder',
      fieldSize: 'small',
    },
  },
  accountBasics: {
    ...editUserFields,
    schoolName: {
      type: 'static',
      labelId: 'form.accountBasics.field.schoolName.label',
      fieldWidth: 'half',
      fieldSize: 'small',
    },
    role: {
      type: 'static',
      labelId: 'form.accountBasics.field.role.label',
      fieldWidth: 'half',
      fieldSize: 'small',
    },
  },
  editTeacher: {
    ...editUserFields,
    role: {
      type: 'static',
      labelId: 'form.editTeacher.field.role.label',
      fieldWidth: 'half',
      fieldSize: 'small',
    },
  },
  editStudent: {
    ...editUserFields,
    family_name: {
      ...editUserFields.family_name,
      required: false,
      minLength: undefined,
    },
    username: {
      ...editUserFields.username,
      type: 'text',
      labelId: 'form.editStudent.field.username.label',
      autocomplete: 'username',
      minLength: 3,
      pattern: '^\\S*$',
      noteId: 'form.editStudent.field.username.note',
    },
  },
  subscriptionPurchase: {
    cardholders_name: {
      ...FIELDS.full_name,
      label: 'Cardholder’s name',
      fieldWidth: 'full',
      fieldSize: 'large',
      required: true,
    },
    credit_card: {
      ...FIELDS.credit_card,
      label: 'Credit card',
      fieldWidth: 'full',
      fieldSize: 'large',
      required: true,
    },
    billing_name: {
      labelId: 'form.subscriptionPurchase.field.billing_name.label',
      type: 'text',
      fieldSize: 'large',
      fieldWidth: 'full',
    },
    billing_email: {
      // labelId: 'form.subscriptionPurchase.field.billing_email.label',
      label: 'Billing email',
      type: 'email',
      fieldSize: 'large',
      fieldWidth: 'half',
    },
    billing_phone: {
      // labelId: 'form.subscriptionPurchase.field.billing_phone.label',
      label: 'Billing phone',
      type: 'tel',
      fieldSize: 'large',
      fieldWidth: 'half',
    },
  },
  addPaymentMethod: {
    cardholders_name: {
      ...FIELDS.full_name,
      label: 'Cardholder’s name',
      fieldWidth: 'full',
      fieldSize: 'large',
      required: true,
    },
    credit_card: {
      ...FIELDS.credit_card,
      label: 'Credit card',
      fieldWidth: 'full',
      fieldSize: 'large',
      required: true,
    },
  },
};
